import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';

const PLAN_BADGE = {
  free: 'bg-[#f5f5f5] text-[#808080] text-xs px-2 py-0.5 rounded-full font-medium',
  pro: 'bg-[#171717] text-white text-xs px-2 py-0.5 rounded-full font-medium',
  lab: 'bg-[#0a72ef] text-white text-xs px-2 py-0.5 rounded-full font-medium',
  enterprise: 'bg-[#de1d8d] text-white text-xs px-2 py-0.5 rounded-full font-medium',
};

const STATUS_BADGE = {
  active: 'bg-[#f0fdf4] text-[#16a34a] text-xs px-2 py-0.5 rounded-full font-medium',
  past_due: 'bg-[#fef2f2] text-[#dc2626] text-xs px-2 py-0.5 rounded-full font-medium',
  canceled: 'bg-[#f5f5f5] text-[#808080] text-xs px-2 py-0.5 rounded-full font-medium',
};

function formatDate(ts) {
  return new Date(ts * 1000).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function CardSectionHeading({ children }) {
  return (
    <h2
      className="text-sm font-semibold text-[#171717] pb-3 mb-4"
      style={{ boxShadow: 'rgba(0,0,0,0.08) 0px -1px 0px 0px inset' }}
    >
      {children}
    </h2>
  );
}

function Card({ children }) {
  return (
    <div
      className="bg-white rounded-xl p-6"
      style={{
        boxShadow:
          'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 2px, #fafafa 0px 0px 0px 1px inset',
      }}
    >
      {children}
    </div>
  );
}

function PlanCard({ user }) {
  const planKey = user.plan || 'free';
  const badgeClass = PLAN_BADGE[planKey] || PLAN_BADGE.free;

  return (
    <Card>
      <CardSectionHeading>Your Plan</CardSectionHeading>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-[#171717] capitalize">{planKey}</span>
          <span className={badgeClass}>{planKey.charAt(0).toUpperCase() + planKey.slice(1)}</span>
        </div>
        {planKey === 'free' && (
          <Link
            to="/pricing"
            className="bg-[#171717] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#2e2e2e] transition-colors"
          >
            Upgrade to Pro
          </Link>
        )}
      </div>
      {planKey !== 'free' && user.ai_heavy_usage_limit > 0 && (
        <p className="mt-3 text-sm text-[#808080]">
          {user.ai_heavy_usage_count} / {user.ai_heavy_usage_limit} AI credits used this month
        </p>
      )}
    </Card>
  );
}

function SubscriptionCard() {
  const [sub, setSub] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [subError, setSubError] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setSubLoading(true);
    setSubError('');
    api
      .get('/api/billing/subscription')
      .then((res) => {
        if (!cancelled) setSub(res.data);
      })
      .catch((err) => {
        if (!cancelled)
          setSubError(err.response?.data?.detail || 'Could not load subscription details.');
      })
      .finally(() => {
        if (!cancelled) setSubLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleManageBilling = async () => {
    setPortalLoading(true);
    setPortalError('');
    try {
      const res = await api.post('/api/billing/create-portal-session');
      window.location.href = res.data.url;
    } catch (err) {
      setPortalError(err.response?.data?.detail || 'Could not open billing portal. Please try again.');
      setPortalLoading(false);
    }
  };

  const statusBadgeClass =
    STATUS_BADGE[sub?.status] || 'bg-[#f5f5f5] text-[#808080] text-xs px-2 py-0.5 rounded-full font-medium';

  return (
    <Card>
      <CardSectionHeading>Subscription</CardSectionHeading>

      {subLoading && (
        <p className="text-sm text-[#808080]">Loading subscription details...</p>
      )}

      {!subLoading && subError && (
        <p className="text-sm text-[#dc2626]">{subError}</p>
      )}

      {!subLoading && !subError && sub && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#808080] w-16 flex-shrink-0">Status</span>
            <span className={statusBadgeClass}>
              {sub.status
                ? sub.status.charAt(0).toUpperCase() + sub.status.slice(1).replace('_', ' ')
                : 'Unknown'}
            </span>
          </div>

          {sub.current_period_end && (
            <div>
              {sub.cancel_at_period_end ? (
                <div className="space-y-1">
                  <p className="text-sm text-[#4d4d4d]">
                    Cancels on{' '}
                    <span className="font-medium text-[#171717]">
                      {formatDate(sub.current_period_end)}
                    </span>
                  </p>
                  <p className="text-xs text-[#808080]">
                    Your subscription will not renew after this date.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-[#4d4d4d]">
                  Renews on{' '}
                  <span className="font-medium text-[#171717]">
                    {formatDate(sub.current_period_end)}
                  </span>
                </p>
              )}
            </div>
          )}

          <div className="pt-1">
            <button
              onClick={handleManageBilling}
              disabled={portalLoading}
              className="bg-[#171717] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#2e2e2e] transition-colors disabled:opacity-50"
            >
              {portalLoading ? 'Opening portal...' : 'Manage Billing'}
            </button>
            {portalError && (
              <p className="mt-2 text-xs text-[#dc2626]">{portalError}</p>
            )}
            <p className="mt-2 text-xs text-[#808080]">
              Opens Stripe — manage payment method, invoices, and cancellation.
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}

function AccountCard({ user }) {
  return (
    <Card>
      <CardSectionHeading>Account</CardSectionHeading>
      <div className="flex items-center gap-3 mb-4">
        <span className="text-sm text-[#808080] w-10 flex-shrink-0">Email</span>
        <span className="text-sm text-[#808080]">{user.email}</span>
      </div>
      <Link
        to="/pricing"
        className="text-[#4d4d4d] px-4 py-2 rounded-md text-sm font-medium hover:text-[#171717] inline-block"
        style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
      >
        View plans &amp; pricing
      </Link>
    </Card>
  );
}

export default function Billing() {
  const { user, loading } = useAuth();

  const showSubscriptionCard =
    user && (user.plan !== 'free' || user.stripe_subscription_id);

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <header className="bg-white" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px -1px 0px 0px inset' }}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="text-base font-semibold text-[#171717]"
            style={{ letterSpacing: '-0.32px' }}
          >
            Protovera
          </Link>
          <Link to="/app" className="text-sm text-[#4d4d4d] font-medium hover:text-[#171717]">
            Go to App →
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-10">
        {loading ? (
          <p className="text-sm text-[#808080]">Loading...</p>
        ) : user ? (
          <div className="space-y-6">
            <PlanCard user={user} />
            {showSubscriptionCard && <SubscriptionCard />}
            <AccountCard user={user} />
          </div>
        ) : null}
      </main>
    </div>
  );
}
